import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type ArtefactOutline, type Folder, type PartItem } from './api'
import type { PartKind } from './grid'
import { artefactTypeOf, type ArtefactType } from './artefact-type'

/**
 * The bid outline — the artefact/part tree together with its display titles.
 *
 * Two surfaces name a part: the left nav (the tree itself) and the breadcrumb
 * (⌂ › bid › artefact › section). Both read the SAME TanStack Query keys
 * (`['docs', bid]` / `['working-docs', bid]`), so mounting this hook twice costs
 * no extra request — it just shares the cache entry.
 */
export interface Outline {
  /** Manually-created artefacts first, then the two synthetic workflow ones. */
  artefacts: ArtefactOutline[]
  /** The explorer's virtual folders, flat (the left nav assembles the tree). */
  folders: Folder[]
  /** True only until the first fetch ever settles (success OR error). Deliberately not
   *  `isLoading`: that flag reverts to `true` on every background poll for a query that has
   *  never once succeeded, which flickered the nav between "…" and "no artefacts yet" every
   *  `refetchInterval` tick instead of staying silent. */
  loading: boolean
}

/**
 * Strip the workflow rooms out of the server's artefacts — they are re-grouped into the two
 * synthetic artefacts ("working"/"review") and must not also show up under the artefact whose
 * middle segment they share.
 *
 * An artefact left with NO parts by that strip held nothing else, so it would render as an empty
 * duplicate of what the synthetic pair already shows — dropped. An artefact that arrives with no
 * parts AT ALL is a different thing entirely: a real, deliberately empty artefact (created without
 * a default part, or its last part deleted), and it stays — hiding it made a member's brand-new
 * artefact look like it was never created.
 *
 * Shared with BidHome, which lists the same artefacts as cards and has to make the same call.
 */
export function withoutWorkflowRooms<T extends { parts: { name: string }[] }>(
  artefacts: T[],
  workingRooms: Set<string>,
): T[] {
  return artefacts
    .filter((d) => d.parts.length === 0 || d.parts.some((p) => !workingRooms.has(p.name)))
    .map((d) => ({ ...d, parts: d.parts.filter((p) => !workingRooms.has(p.name)) }))
}

export function useOutline(bid: string | null): Outline {
  const { t } = useLang()

  const docs = useQuery({
    queryKey: ['docs', bid],
    queryFn: () => api.artefacts(bid as string),
    enabled: Boolean(bid),
    // Outline has no live CRDT channel: poll so external edits (e.g. the console agent
    // renaming/creating parts) appear without a manual refresh.
    refetchInterval: 4000,
  })
  const baseArtefacts = docs.data?.artefacts ?? []

  // Working artefacts (one artifact + one review per workflow node)
  const workingDocsQuery = useQuery({
    queryKey: ['working-docs', bid],
    queryFn: () => api.workingDocs(bid as string),
    enabled: Boolean(bid),
  })
  const workingNodes = workingDocsQuery.data?.nodes ?? []

  // Explorer folders. Polled like the artefacts: the grouping is shared, so a folder a teammate
  // creates has to show up here without a manual refresh.
  const foldersQuery = useQuery({
    queryKey: ['folders', bid],
    queryFn: () => api.folders(bid as string),
    enabled: Boolean(bid),
    refetchInterval: 4000,
  })

  // Keep workflow rooms out of manually-created artefacts to avoid duplicates.
  const workingRooms = new Set(workingNodes.flatMap((n) => [n.artifactRoom, n.reviewRoom]))
  const manualArtefacts = withoutWorkflowRooms(baseArtefacts, workingRooms)

  // Two synthetic artefacts built from workflow nodes: one for artifact rooms
  // ("Artefact de travail") and one for review rooms ("Artefact de revue").
  // They are read-only in the nav (no rename/delete/add-item) and, having no artefact row to
  // carry a placement, they always sit at the explorer's root.
  const syntheticArtefacts = workingNodes.length > 0
    ? [
        {
          artefactId: '__travail__',
          bidId: bid ?? '',
          title: t('doc_travail'),
          folderId: null,
          parts: workingNodes.map((n) => ({ partId: n.nodeId, name: n.artifactRoom, title: n.section })),
        },
        {
          artefactId: '__revue__',
          bidId: bid ?? '',
          title: t('doc_revue'),
          folderId: null,
          parts: workingNodes.map((n) => ({ partId: n.nodeId, name: n.reviewRoom, title: n.section })),
        },
      ]
    : []

  return {
    artefacts: [...manualArtefacts, ...syntheticArtefacts],
    folders: foldersQuery.data?.folders ?? [],
    loading: !docs.isFetched,
  }
}

/** Display titles for an open room: the containing artefact and the part ("section"). */
export interface RoomTitles {
  artefact: string
  section: string
}

/**
 * Resolve a room key ("bid/artefact/part") to its display titles. Falls back to the raw key
 * segments while the outline is still loading (or when the room is unknown), so the crumb
 * never renders empty.
 */
export function roomTitles(artefacts: ArtefactOutline[], room: string): RoomTitles {
  for (const artefact of artefacts) {
    const part = artefact.parts.find((p) => p.name === room)
    if (part) return { artefact: artefact.title, section: part.title }
  }
  const segs = room.split('/')
  return { artefact: segs[1] ?? room, section: segs[2] ?? room }
}

/**
 * What a room CONTAINS, off the outline — the input to the one render decision in the app
 * (Canvas mounts RichEditor or GridEditor on it).
 *
 * The room's own `Y.Map('meta')` is the authority, but it only arrives after the first sync, so
 * branching on it would flash the wrong editor on every open. A room the outline does not know
 * (still loading, or a workflow room) reads as prose — today's behaviour, and what an older
 * server's outline implies.
 */
export function partKindOf(artefacts: ArtefactOutline[], room: string): PartKind {
  for (const artefact of artefacts) {
    const part = artefact.parts.find((p) => p.name === room)
    if (part) return part.kind ?? 'markdown'
  }
  return 'markdown'
}

/** The outline entry of a room, or null when the outline does not know it (still loading, a
 *  workflow room). */
export function partOf(artefacts: ArtefactOutline[], room: string): PartItem | null {
  for (const artefact of artefacts) {
    const part = artefact.parts.find((p) => p.name === room)
    if (part) return part
  }
  return null
}

/**
 * The type of the artefact holding a room (AB#197) — what the export dialog offers for the
 * deliverable it belongs to. A room the outline does not know (still loading, or a workflow room)
 * reads as prose, like an artefact from an older server.
 */
export function artefactTypeOfRoom(artefacts: ArtefactOutline[], room: string): ArtefactType {
  const artefact = artefacts.find((a) => a.parts.some((p) => p.name === room))
  return artefactTypeOf(artefact?.type)
}

/**
 * The label shown next to a part's § badge (explorer + dashboard) — the title, with a leading
 * hand-typed ordinal ("1. Recommandation") stripped when the badge already carries that number.
 * Some bids (built from already-numbered Word/PDF sources) have the ordinal baked into the
 * STORED title as plain text — independent of both the heading text and the computed `section`
 * range (see backend/app/services.ts#withSectionNumbers), which is recomputed from the CURRENT
 * part order on every read. After a reorder the two can openly disagree (AB#30) — worse than the
 * plain redundancy of matching. The badge is the single authoritative number; this only changes
 * what is DISPLAYED beside it, never the stored title, so renaming a part still shows and edits
 * the original text unchanged.
 *
 * No badge (`section` null/undefined) → nothing to be redundant with, title passed through as-is.
 */
export function partLabel(p: { title: string; section?: string | null }): string {
  if (!p.section) return p.title
  return p.title.replace(/^\d+\.\s+/, '')
}
